/**
 * Tamagui Theme Components
 * Platform-agnostic components using Tamagui primitives
 */

import { styled, YStack, XStack, Text, Input as TamaguiInput, GetProps } from 'tamagui';
import type { StatusType, RiskLevel, TrafficColor } from '@/lib/theme-config';
import { STATUS_CONFIG, RISK_CONFIG } from '@/lib/theme-config';

// Base styled components with traffic light theme
export const Card = styled(YStack, {
  borderRadius: '$3',
  backgroundColor: '$card',
  borderWidth: 1,
  borderColor: '$border',
  padding: '$4',
  shadowColor: 'rgba(0,0,0,0.5)',
  shadowOffset: { width: 0, height: 2 },
  shadowOpacity: 0.25,
  shadowRadius: 8,

  variants: {
    glow: {
      green: {
        shadowColor: '$trafficGreen',
        shadowOpacity: 0.3,
      },
      red: {
        shadowColor: '$trafficRed',
        shadowOpacity: 0.3,
      },
      yellow: {
        shadowColor: '$trafficYellow',
        shadowOpacity: 0.3,
      },
      cyan: {
        shadowColor: '$secondary',
        shadowOpacity: 0.2,
      },
    },
  } as const,
});

// Traffic Light Indicator
interface TrafficLightProps extends GetProps<typeof XStack> {
  active?: TrafficColor | 'all';
  orientation?: 'vertical' | 'horizontal';
}

const TrafficDot = styled(YStack, {
  borderRadius: 999,
  backgroundColor: '$muted',

  variants: {
    size: {
      sm: { width: 8, height: 8 },
      md: { width: 12, height: 12 },
      lg: { width: 16, height: 16 },
    },
    color: {
      red: { backgroundColor: '$trafficRed' },
      yellow: { backgroundColor: '$trafficYellow' },
      green: { backgroundColor: '$trafficGreen' },
      inactive: { backgroundColor: '$muted', opacity: 0.3 },
    },
  } as const,

  defaultVariants: {
    size: 'md',
    color: 'inactive',
  },
});

export const TrafficLight = ({
  active = 'all',
  orientation = 'vertical',
  ...props
}: TrafficLightProps) => {
  const Container = orientation === 'vertical' ? YStack : XStack;
  const isActive = (color: TrafficColor) => active === 'all' || active === color;

  return (
    <Container
      backgroundColor="$card"
      borderRadius="$2"
      borderWidth={1}
      borderColor="$border"
      padding="$2"
      gap="$2"
      {...props}
    >
      <TrafficDot color={isActive('red') ? 'red' : 'inactive'} />
      <TrafficDot color={isActive('yellow') ? 'yellow' : 'inactive'} />
      <TrafficDot color={isActive('green') ? 'green' : 'inactive'} />
    </Container>
  );
};

// Badge Component
interface BadgeProps extends GetProps<typeof XStack> {
  variant?: 'status' | 'risk';
  status?: StatusType;
  level?: RiskLevel;
  label?: string;
  className?: string;
  children?: React.ReactNode;
}

const BadgeBase = styled(XStack, {
  borderRadius: 999,
  paddingHorizontal: '$3',
  paddingVertical: '$1.5',
  borderWidth: 1,
  alignItems: 'center',
  gap: '$2',

  variants: {
    type: {
      success: {
        backgroundColor: '$trafficGreen',
        borderColor: '$trafficGreen',
        opacity: 0.9,
      },
      warning: {
        backgroundColor: '$trafficYellow',
        borderColor: '$trafficYellow',
        opacity: 0.9,
      },
      error: {
        backgroundColor: '$trafficRed',
        borderColor: '$trafficRed',
        opacity: 0.9,
      },
      info: {
        backgroundColor: '$secondary',
        borderColor: '$secondary',
        opacity: 0.8,
      },
      critical: {
        backgroundColor: '$trafficRed',
        borderColor: '$trafficRed',
        opacity: 0.9,
      },
      high: {
        backgroundColor: '$trafficOrange',
        borderColor: '$trafficOrange',
        opacity: 0.9,
      },
      medium: {
        backgroundColor: '$trafficYellow',
        borderColor: '$trafficYellow',
        opacity: 0.8,
      },
      low: {
        backgroundColor: '$trafficGreen',
        borderColor: '$trafficGreen',
        opacity: 0.8,
      },
      failed: {
        backgroundColor: '#475569',
        borderColor: '#64748b',
        opacity: 0.85,
      },
    },
  } as const,
});

export const Badge = ({
  variant,
  status,
  level,
  label,
  className,
  children,
  ...props
}: BadgeProps) => {
  let displayLabel = label || children;
  let type: string = 'info';

  if (variant === 'status' && status) {
    const config = STATUS_CONFIG[status];
    type = status;
    displayLabel = displayLabel || config?.label;
  } else if (variant === 'risk' && level) {
    const config = RISK_CONFIG[level];
    type = level;
    displayLabel = displayLabel || config?.label;
  }

  return (
    <BadgeBase type={type as any} className={className} {...props}>
      {typeof displayLabel === 'string' ? (
        <Text
          fontSize="$2"
          fontWeight="600"
          textTransform="uppercase"
          letterSpacing={0.5}
          color="white"
        >
          {displayLabel}
        </Text>
      ) : (
        displayLabel
      )}
    </BadgeBase>
  );
};

// Button Component
export const Button = styled(YStack, {
  borderRadius: '$3',
  paddingHorizontal: '$4',
  paddingVertical: '$2.5',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  userSelect: 'none',

  variants: {
    variant: {
      default: {
        backgroundColor: '$primary',
        hoverStyle: { opacity: 0.9 },
        pressStyle: { scale: 0.98 },
      },
      outline: {
        backgroundColor: 'transparent',
        borderWidth: 1,
        borderColor: '$border',
        hoverStyle: { backgroundColor: '$muted' },
      },
      ghost: {
        backgroundColor: 'transparent',
        hoverStyle: { backgroundColor: '$muted' },
      },
      green: {
        backgroundColor: '$trafficGreen',
        hoverStyle: { opacity: 0.9 },
        pressStyle: { scale: 0.98 },
      },
      red: {
        backgroundColor: '$trafficRed',
        hoverStyle: { opacity: 0.9 },
        pressStyle: { scale: 0.98 },
      },
      yellow: {
        backgroundColor: '$trafficYellow',
        hoverStyle: { opacity: 0.9 },
        pressStyle: { scale: 0.98 },
      },
    },
    size: {
      sm: {
        paddingHorizontal: '$3',
        paddingVertical: '$2',
      },
      md: {
        paddingHorizontal: '$4',
        paddingVertical: '$2.5',
      },
      lg: {
        paddingHorizontal: '$6',
        paddingVertical: '$3',
      },
    },
  } as const,

  defaultVariants: {
    variant: 'default',
    size: 'md',
  },
});

// Input Component
export const Input = styled(TamaguiInput, {
  borderRadius: '$3',
  borderWidth: 1,
  borderColor: '$border',
  backgroundColor: '$background',
  paddingHorizontal: '$3',
  paddingVertical: '$2',
  color: '$foreground',
  fontSize: '$4',

  focusStyle: {
    borderColor: '$primary',
    outlineWidth: 2,
    outlineColor: '$primary',
    outlineStyle: 'solid',
  },

  variants: {
    error: {
      true: {
        borderColor: '$trafficRed',
      },
    },
  } as const,
}) as unknown as typeof TamaguiInput;

// Container components
export const Container = styled(YStack, {
  maxWidth: 1280,
  marginHorizontal: 'auto',
  paddingHorizontal: '$4',
  width: '100%',
});

export const Section = styled(YStack, {
  paddingVertical: '$8',
});

// Export Text variants
export const Heading = styled(Text, {
  fontWeight: '700',
  letterSpacing: -0.5,

  variants: {
    level: {
      1: { fontSize: '$10' },
      2: { fontSize: '$8' },
      3: { fontSize: '$7' },
      4: { fontSize: '$6' },
    },
  } as const,
});

export const Label = styled(Text, {
  fontSize: '$3',
  fontWeight: '600',
  color: '$mutedForeground',
});

export const Body = styled(Text, {
  fontSize: '$4',
  lineHeight: '$4',
  color: '$foreground',
});
